import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { transformRoute } from "helpers";

import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { VehicleAlertRow } from "services/api/dashboard/types";
import { getVehicleAlertsAPI } from "services/api/dashboard/api";
import {
  useHandleTextFieldChange,
  useHandleDropdownChange,
} from "services/forms/hooks";

import { TableSearch } from "ui/components/Table/TableSearch";
import { NoResults } from "ui/components/NoResults";
import { TextField } from "ui/components/TextField/TextField";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

import { ALERTS_SEARCH_COLUMNS } from "./consts";

interface AlertsFilters {
  search: string;
  type: string;
  status: string;
}

const initialFilters: AlertsFilters = { search: "", type: "", status: "" };

const TYPE_OPTIONS: DropdownOption[] = [
  { value: "", text: "Svi tipovi" },
  { value: "registration", text: "Registracija" },
  { value: "insurance", text: "Osiguranje" },
  { value: "casco", text: "Kasko" },
  { value: "fireextinguisher", text: "Vatrogasni aparat" },
];

const STATUS_OPTIONS: DropdownOption[] = [
  { value: "", text: "Svi statusi" },
  { value: "expired", text: "Isteklo" },
  { value: "expiring", text: "Pred istek" },
];

const AlertsSearchTab: React.FC = () => {
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleAlertRow>
  >(initialDataWithPagination);
  const [filters, setFilters] = useState<AlertsFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchResults = useCallback(
    async (pagination: Pagination, f: AlertsFilters) => {
      setIsLoading(true);
      try {
        setSearchResults(
          await getVehicleAlertsAPI(pagination, {
            search: f.search,
            type: f.type,
            status: f.status,
          })
        );
      } catch {
        setSearchResults(initialDataWithPagination);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Dohvati na mount + na svaku promjenu filtera.
  useEffect(() => {
    fetchResults(initialDataWithPagination.pagination, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleTextFieldChange = useHandleTextFieldChange(setFilters, filters);
  const handleDropdownChange = useHandleDropdownChange(setFilters, filters);

  const handlePaginationChange = (pagination: Pagination) => {
    fetchResults(pagination, filters);
  };

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(
        transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
          id: vehicleId,
        })
      );
    },
    [navigate]
  );

  return (
    <div className="px-4 py-4">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="w-[180px]">
          <TextField
            type="text"
            name="search"
            value={filters.search}
            onChange={handleTextFieldChange}
            placeholder="Pretraži..."
            startIcon="/media/icons/search.svg"
            cssClassName="bg-white"
          />
        </div>
        <div className="w-[160px]">
          <Dropdown
            name="type"
            value={filters.type}
            options={TYPE_OPTIONS}
            onChange={handleDropdownChange}
            placeholder="Tip"
          />
        </div>
        <div className="w-[150px]">
          <Dropdown
            name="status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={handleDropdownChange}
            placeholder="Status"
          />
        </div>
      </div>

      {!isLoading && searchResults.pagination.totalPages === 0 ? (
        <NoResults message="Nema upozorenja" />
      ) : (
        <TableSearch
          columns={ALERTS_SEARCH_COLUMNS(handleVehicleClick)}
          data={searchResults.data}
          selectableRows={false}
          pagination={searchResults.pagination}
          isLoading={isLoading}
          onPaginationChange={handlePaginationChange}
        />
      )}
    </div>
  );
};

export default AlertsSearchTab;
